import React, { useState, useEffect } from 'react';
import { roomsAPI } from './services/api';
import './RoomSelectionModal.css';

interface RoomSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  roomName: string;
  checkInDate: string;
  checkOutDate?: string;
  initialAdults?: number;
  initialChildren?: number;
  initialChildAges?: number[];
  onConfirm: (data: {
    roomId: string;
    checkInDate: string;
    checkOutDate: string;
    adults: number;
    children: number;
    childAges: number[];
  }) => void;
}

const EMPTY_CHILD_AGES: number[] = [];

const parseChildAgesKey = (key: string): number[] => {
  if (!key) return EMPTY_CHILD_AGES;
  return key
    .split(',')
    .map((value) => Number(value))
    .filter((age) => Number.isFinite(age) && age >= 0);
};

const RoomSelectionModal: React.FC<RoomSelectionModalProps> = ({
  isOpen,
  onClose,
  roomId,
  roomName,
  checkInDate,
  checkOutDate: initialCheckOut,
  initialAdults = 2,
  initialChildren = 0,
  initialChildAges,
  onConfirm
}) => {
  const initialChildAgesKey = Array.isArray(initialChildAges) ? initialChildAges.join(',') : '';
  const [checkOutDate, setCheckOutDate] = useState('');
  const [adults, setAdults] = useState(Math.max(1, initialAdults));
  const [children, setChildren] = useState(Math.max(0, initialChildren));
  const [childAges, setChildAges] = useState<number[]>(parseChildAgesKey(initialChildAgesKey));
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [originalPrice, setOriginalPrice] = useState<number | null>(null);
  const [discountLabel, setDiscountLabel] = useState('');
  const [capacityWarning, setCapacityWarning] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    setAdults(Math.max(1, initialAdults));
    setChildren(Math.max(0, initialChildren));
    const normalizedInitialChildAges = parseChildAgesKey(initialChildAgesKey);
    if (initialChildren > 0) {
      const nextAges = Array.from({ length: initialChildren }, (_, index) => {
        const age = normalizedInitialChildAges[index];
        return typeof age === 'number' ? age : 7;
      });
      setChildAges(nextAges);
    } else {
      setChildAges([]);
    }

    if (initialCheckOut) {
      setCheckOutDate(initialCheckOut);
    } else if (checkInDate) {
      const nextDay = new Date(checkInDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setCheckOutDate(nextDay.toISOString().split('T')[0]);
    }
  }, [isOpen, checkInDate, initialCheckOut, initialAdults, initialChildren, initialChildAgesKey]);

  useEffect(() => {
    if (!isOpen) return;
    setChildAges((prev) => {
      if (children <= 0) return [];
      if (prev.length === children) return prev;
      if (children > prev.length) {
        return [...prev, ...Array(children - prev.length).fill(7)];
      }
      return prev.slice(0, children);
    });
  }, [children, isOpen]);

  useEffect(() => {
    if (!isOpen || !checkInDate || !checkOutDate) return;

    let cancelled = false;

    const fetchPricePreview = async () => {
      setPriceLoading(true);
      setPriceError('');
      setCapacityWarning('');

      try {
        const response = await roomsAPI.getAvailability({
          checkIn: checkInDate,
          checkOut: checkOutDate,
          adults,
          children,
          childrenAges: childAges.join(',')
        });

        const rooms = response.data?.data || [];
        const matchedRoom = rooms.find((room: any) => room.roomTypeId === roomId || room.id === roomId);

        if (cancelled) return;

        if (!matchedRoom || typeof matchedRoom.price !== 'number') {
          setEstimatedPrice(null);
          setOriginalPrice(null);
          setDiscountLabel('');
          setPriceError('Bu kişi sayısı için uygun oda bulunamadı. Lütfen kişi sayısını veya tarihi değiştirin.');
          return;
        }

        setEstimatedPrice(matchedRoom.price);
        setOriginalPrice(typeof matchedRoom.originalPrice === 'number' ? matchedRoom.originalPrice : null);
        setDiscountLabel(matchedRoom.discountName || matchedRoom.discountType || '');

        const standardCapacity = matchedRoom.standardCapacity || matchedRoom.maxAdults || matchedRoom.maxOccupancy || adults;
        const maxOccupancy = matchedRoom.maxOccupancy || standardCapacity;
        const totalGuests = adults + children;

        if (totalGuests > maxOccupancy) {
          setCapacityWarning('Seçilen kişi sayısı bu oda için sınırı aşıyor.');
        } else if (totalGuests > standardCapacity) {
          setCapacityWarning(`Bu oda için ${standardCapacity} kişi üzeri ek yatak / onay gerekebilir.`);
        }
      } catch (error) {
        if (cancelled) return;
        setEstimatedPrice(null);
        setOriginalPrice(null);
        setDiscountLabel('');
        setPriceError('Fiyat önizlemesi alınamadı. Tarih veya kişi bilgilerini değiştirip tekrar deneyin.');
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    };

    fetchPricePreview();

    return () => {
      cancelled = true;
    };
  }, [isOpen, checkInDate, checkOutDate, adults, children, childAges, roomId]);

  const handleChildAgeChange = (index: number, age: number) => {
    const newAges = [...childAges];
    newAges[index] = age;
    setChildAges(newAges);
  };

  const handleConfirm = () => {
    if (!checkOutDate || new Date(checkOutDate) <= new Date(checkInDate)) {
      alert('Çıkış tarihi, giriş tarihinden sonra olmalıdır');
      return;
    }

    if (children > 0 && childAges.some(age => age === 0)) {
      alert('Lütfen tüm çocukların yaşlarını girin');
      return;
    }

    onConfirm({
      roomId,
      checkInDate,
      checkOutDate,
      adults,
      children,
      childAges
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateNights = () => {
    if (!checkInDate || !checkOutDate) return 0;
    const diff = new Date(checkOutDate).getTime() - new Date(checkInDate).getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const nights = calculateNights();
  const totalGuests = adults + children;
  const canProceed = !priceLoading && !capacityWarning.includes('sınırı aşıyor');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Kapat">
          ✕
        </button>

        <div className="modal-header">
          <h2>Rezervasyon Bilgileri</h2>
          <p className="room-name">{roomName}</p>
        </div>

        <div className="modal-body">
          <div className="date-section">
            <div className="date-row">
              <div className="date-item">
                <label>Giriş Tarihi</label>
                <div className="date-value">{formatDate(checkInDate)}</div>
              </div>
              <div className="date-separator">→</div>
              <div className="date-item">
                <label>Çıkış Tarihi</label>
                <input
                  type="date"
                  value={checkOutDate}
                  min={checkInDate}
                  onChange={(e) => setCheckOutDate(e.target.value)}
                  className="date-input"
                />
              </div>
            </div>
            {nights > 0 && (
              <div className="nights-info">
                {nights} gece konaklama
              </div>
            )}
          </div>

          <div className="guests-section">
            <h3>Misafir Bilgileri</h3>
            <div className="guest-summary-chip">
              <span>{adults} yetişkin</span>
              <span>{children} çocuk</span>
              <span>{totalGuests} kişi</span>
            </div>
            <div className="guest-inputs-row">
              <div className="guest-input-group">
                <label>YETİŞKİN</label>
                <select value={adults} onChange={(e) => setAdults(parseInt(e.target.value))} className="guest-select">
                  {[1, 2, 3].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              <div className="guest-input-group">
                <label>ÇOCUK</label>
                <select value={children} onChange={(e) => setChildren(parseInt(e.target.value))} className="guest-select">
                  {[0, 1, 2].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>

            {children > 0 && (
              <div className="children-ages-row">
                <label>Çocuk Yaşları:</label>
                <div className="ages-grid">
                  {childAges.map((age, index) => (
                    <select
                      key={index}
                      className="age-select"
                      value={age}
                      onChange={(e) => handleChildAgeChange(index, parseInt(e.target.value))}
                    >
                      <option value={0}>Yaş seçin</option>
                      {Array.from({ length: 18 }, (_, i) => i).map(ageOption => (
                        <option key={ageOption} value={ageOption}>
                          {ageOption} yaş
                        </option>
                      ))}
                    </select>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="price-summary">
            <div className="price-row">
              <span>Fiyat Önizlemesi</span>
              <span className="price-value">
                {priceLoading ? 'Hesaplanıyor...' : estimatedPrice !== null ? `₺${estimatedPrice.toLocaleString('tr-TR')}` : '—'}
              </span>
            </div>
            {originalPrice !== null && estimatedPrice !== null && originalPrice > estimatedPrice && (
              <div className="price-row">
                <span>Eski fiyat</span>
                <span className="price-value" style={{ textDecoration: 'line-through', color: '#9ca3af' }}>
                  ₺{originalPrice.toLocaleString('tr-TR')}
                </span>
              </div>
            )}
            <div className="price-row">
              <span>Konaklama</span>
              <span className="price-value">{nights} gece</span>
            </div>
            <div className="price-row total">
              <span>{discountLabel ? `Güncel Fiyat (${discountLabel})` : 'Güncel Fiyat'}</span>
              <span className="price-value">
                {estimatedPrice !== null ? `₺${estimatedPrice.toLocaleString('tr-TR')}` : '—'}
              </span>
            </div>
            <p className="price-note">Nihai toplam, rezervasyon adımında seçilen opsiyonlara göre güncellenir.</p>
            {priceError && <p className="price-note" style={{ color: '#b91c1c' }}>{priceError}</p>}
            {capacityWarning && <p className="price-note" style={{ color: '#b45309' }}>{capacityWarning}</p>}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            İptal
          </button>
          <button className="btn-primary" onClick={handleConfirm} disabled={!canProceed}>
            Rezervasyona Devam Et
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomSelectionModal;
